import { Purchases } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';

const REVENUECAT_APPLE_KEY = import.meta.env.VITE_REVENUECAT_APPLE_KEY || 'appl_YOUR_PUBLIC_KEY';
const REVENUECAT_GOOGLE_KEY = import.meta.env.VITE_REVENUECAT_GOOGLE_KEY || 'goog_YstnxqgvwohHoRSGSYtdQThqbXM';

let isConfigured = false;

/**
 * Inicializa RevenueCat con el identificador del usuario autenticado.
 */
export async function initPurchases(userId) {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  try {
    const platform = Capacitor.getPlatform();
    const apiKey = platform === 'ios' ? REVENUECAT_APPLE_KEY : REVENUECAT_GOOGLE_KEY;

    if (!apiKey || apiKey.startsWith('appl_YOUR_') || apiKey.startsWith('goog_YOUR_')) {
      console.warn('[RevenueCat] Llave pública no configurada aún en variables de entorno.');
      return false;
    }

    await Purchases.configure({
      apiKey,
      appUserID: String(userId || '')
    });

    isConfigured = true;
    console.log('[RevenueCat] Inicializado correctamente para usuario:', userId);
    return true;
  } catch (error) {
    console.error('[RevenueCat] Error al inicializar:', error);
    return false;
  }
}

const FALLBACK_PACKAGES = [
  {
    identifier: '$rc_annual',
    packageType: 'ANNUAL',
    product: {
      title: 'NousMetrics PRO - Anual',
      description: 'Acceso anual con 25% de ahorro (6,58 €/mes)',
      priceString: '79,00 €',
      price: 79.00
    }
  },
  {
    identifier: '$rc_monthly',
    packageType: 'MONTHLY',
    product: {
      title: 'NousMetrics PRO - Mensual',
      description: 'Acceso mensual completo al Copiloto IA y analítica',
      priceString: '8,99 €',
      price: 8.99
    }
  }
];

/**
 * Obtiene las ofertas y paquetes de suscripción activos configurados en RevenueCat / App Store.
 */
export async function getSubscriptionOfferings() {
  if (!Capacitor.isNativePlatform() || !isConfigured) {
    // Retornamos fallback para desarrollo o entorno web
    return {
      isNative: false,
      packages: FALLBACK_PACKAGES
    };
  }

  try {
    const offerings = await Purchases.getOfferings();
    if (offerings.current && offerings.current.availablePackages.length > 0) {
      return {
        isNative: true,
        packages: offerings.current.availablePackages,
        offering: offerings.current
      };
    }
  } catch (error) {
    console.error('[RevenueCat] Error obteniendo ofertas:', error);
  }

  return {
    isNative: Capacitor.isNativePlatform(),
    packages: FALLBACK_PACKAGES
  };
}

/**
 * Ejecuta la compra In-App nativa a través de Apple StoreKit / Google Play.
 */
export async function purchaseSubscriptionPackage(aPackage, apiUrl, token) {
  if (!Capacitor.isNativePlatform() || !isConfigured) {
    throw new Error('Las compras In-App oficiales se gestionan de forma segura a través de Apple App Store / Google Play en la app móvil.');
  }

  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage });
    const activeEnts = customerInfo?.entitlements?.active || {};
    const isPro = Boolean(activeEnts['nous_metrics_pro'] || activeEnts['pro_access'] || Object.keys(activeEnts).length > 0);

    if (isPro && apiUrl && token) {
      // Solicitar al backend que verifique el estado real contra RevenueCat
      await fetch(`${apiUrl}/subscription/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      }).catch(err => console.warn('[RevenueCat] Sync DB aviso:', err));
    }

    return { success: true, isPro, customerInfo };
  } catch (error) {
    if (error.userCancelled) {
      return { cancelled: true };
    }
    console.error('[RevenueCat] Error en el flujo de compra:', error);
    throw error;
  }
}

/**
 * Restaura compras anteriores del usuario (Requisito obligatorio de Apple Guideline 3.1.2).
 */
export async function restoreUserPurchases(apiUrl, token) {
  if (!Capacitor.isNativePlatform() || !isConfigured) {
    throw new Error('La restauración de compras está disponible en la aplicación nativa de iOS y Android.');
  }

  try {
    const customerInfo = await Purchases.restorePurchases();
    const activeEnts = customerInfo?.entitlements?.active || {};
    const isPro = Boolean(activeEnts['nous_metrics_pro'] || activeEnts['pro_access'] || Object.keys(activeEnts).length > 0);

    if (apiUrl && token) {
      await fetch(`${apiUrl}/subscription/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      }).catch(err => console.warn('[RevenueCat] Restore sync aviso:', err));
    }

    return { success: true, isPro, customerInfo };
  } catch (error) {
    console.error('[RevenueCat] Error al restaurar compras:', error);
    throw error;
  }
}
